// @ts-check
// invite-acceptor.js — Handles `?invite=TOKEN` URLs.
//
// Flow:
//  1. On load, if the URL has `?invite=TOKEN`, stash it in sessionStorage
//     and strip it from the URL so refreshes are clean.
//  2. If the user is already authenticated, accept immediately.
//  3. Otherwise, prompt the user to sign in (with the email the invite was
//     issued to), then accept on the next powersync:ready / SIGNED_IN.
//  4. After acceptance, switch to the workspace and reload.

import { getSupabaseClient } from '../storage/session.js';
import { updateStatus } from '../ui/status-pill.js';
import { WorkspaceManager } from './workspace-manager.js';
import { WorkspaceStore } from './workspace-store.js';
import { WorkspaceSwitcher } from './workspace-switcher.js';

// Populated by the IIFE below; exported as the ESM InviteAcceptor.
let _inviteAcceptor: any;

(() => {
  const TOKEN_KEY = 'pending_invite_token';

  /** @returns {string | null} the invite token from `?invite=`, or null. */
  function _readUrlToken() {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('invite');
    if (!t) return null;
    sp.delete('invite');
    const newQuery = sp.toString();
    const newUrl =
      window.location.pathname + (newQuery ? `?${newQuery}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
    return t;
  }

  /**
   * @param {string} msg
   * @param {boolean} [ok]
   * @returns {void}
   */
  function _toast(msg: any, ok: any) {
    if (typeof updateStatus === 'function') updateStatus(msg, !!ok, !ok);
  }

  let _accepting = false;

  /** @returns {Promise<void>} */
  async function _tryAccept() {
    if (_accepting) return;
    if (!WorkspaceManager?.available) return;
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    _accepting = true;
    try {
      // Pre-flight: if the active session is for a different account
      // than the URL hint suggests, the server will reject the accept
      // RPC with the verbose "This invitation is for X but you are
      // signed in as Y" error. We can short-circuit with a clearer
      // message when the URL carries an `email` hint (added by some
      // invite-link templates) and the active session disagrees.
      const urlEmailHint = new URLSearchParams(window.location.search).get('email');
      if (urlEmailHint && getSupabaseClient()) {
        try {
          const session = (await getSupabaseClient().auth.getSession())?.data?.session;
          const sessionEmail = (session?.user?.email || '').toLowerCase();
          const hintEmail = urlEmailHint.toLowerCase();
          if (sessionEmail && hintEmail && sessionEmail !== hintEmail) {
            sessionStorage.removeItem(TOKEN_KEY);
            _toast(
              'This invitation is for ' +
                hintEmail +
                ", but you're signed in as " +
                sessionEmail +
                '. Sign out and try again.',
              false
            );
            return;
          }
        } catch {
          /* ignore — fall through to server-side check */
        }
      }

      _toast('Accepting workspace invitation…', true);
      const wsId = await WorkspaceManager.acceptInvite(token);
      sessionStorage.removeItem(TOKEN_KEY);
      _toast('Joined workspace. Switching…', true);

      // Reconcile must succeed before we can switch — switchTo relies on a
      // workspace entry being present for the new workspace. If it fails
      // we surface the error and require the user to retry, rather than
      // cascading into WorkspaceManager.switchTo (which makes the same
      // network call and would just fail again under the same conditions).
      let reconcileFailed = false;
      try {
        if (WorkspaceSwitcher?.reconcileWorkspaces) {
          await WorkspaceSwitcher.reconcileWorkspaces();
        }
      } catch (recErr: any) {
        console.warn('[invite-acceptor] reconcile after accept failed:', recErr?.message);
        reconcileFailed = true;
      }
      const entry = WorkspaceStore?.findByWorkspace?.(wsId) || null;
      if (entry && WorkspaceSwitcher) {
        await WorkspaceSwitcher.switchTo(entry.id);
      } else if (wsId && !reconcileFailed) {
        // Reconcile succeeded but the entry didn't materialise — server
        // returned the workspace but our id derivation diverged. Use the
        // workspace-only switch as a last resort.
        await WorkspaceManager.switchTo(wsId);
      } else {
        _toast(
          "Joined the workspace, but couldn't load it just now. " +
            'Refresh or open Settings → Workspaces to switch to it.',
          false
        );
      }
    } catch (e: any) {
      sessionStorage.removeItem(TOKEN_KEY);
      _toast(e.message || 'Failed to accept invitation.', false);
    } finally {
      _accepting = false;
    }
  }

  // Capture the token as early as possible so navigation/auth flows that
  // touch window.location don't lose it.
  const captured = _readUrlToken();
  if (captured) {
    sessionStorage.setItem(TOKEN_KEY, captured);
  }

  // Try to accept once the sync layer reports authentication.
  window.addEventListener('powersync:ready', _tryAccept);
  // Also try on DOMContentLoaded in case the user is already signed in
  // (powersync:ready fires before this script runs in some load orders).
  window.addEventListener('DOMContentLoaded', () => {
    setTimeout(_tryAccept, 500);
  });

  // If the user lands while not signed in, surface a status nudge.
  window.addEventListener('powersync:auth-required', () => {
    if (sessionStorage.getItem(TOKEN_KEY)) {
      _toast('Sign in (Settings → ☁️ Sync) with the invited email to join the workspace.', false);
    }
  });

  _inviteAcceptor = { tryAccept: _tryAccept };
})();

// ES-module export. Consumers import { InviteAcceptor } from this file; the
// window.InviteAcceptor back-compat shim has been retired (all readers are ESM).
export const InviteAcceptor = _inviteAcceptor;
